import { i18nKeyForError, parseApiError } from '@features/allocation';
import { AlertDialog, useToast } from '@ntconsult/aluma-react';
import { useTranslation } from 'react-i18next';
import { useDeleteAllocationTag } from '../hooks/useDeleteAllocationTag';
import type { AllocationTag } from '../types/AllocationTag';

type DeleteAllocationTagDialogProps = {
  tag: AllocationTag | null;
  onClose: () => void;
};

/** Tag em uso não é excluída (como no legado): o diálogo só explica e aponta a desativação. */
export function DeleteAllocationTagDialog({ tag, onClose }: DeleteAllocationTagDialogProps) {
  const { t } = useTranslation(['tagsAlocacao', 'alocacao', 'common']);
  const { toast } = useToast();
  const deleteMutation = useDeleteAllocationTag();
  const inUse = !!tag && tag.allocationCount > 0;

  function confirm() {
    if (!tag) return;
    deleteMutation.mutate(tag.id, {
      onSuccess: () => {
        toast.success({ title: t('tagsAlocacao:toasts.excluirSucesso') });
        onClose();
      },
      onError: (error) => {
        const apiError = parseApiError(error);
        toast.danger({
          title: t((apiError ? i18nKeyForError(apiError.code) : 'alocacao:toasts.erro') as never),
        });
        onClose();
      },
    });
  }

  return (
    <AlertDialog open={!!tag} onOpenChange={(next) => !next && onClose()}>
      <AlertDialog.Content>
        <AlertDialog.Title>
          {t('tagsAlocacao:excluir.titulo', { nome: tag?.name ?? '' })}
        </AlertDialog.Title>
        <AlertDialog.Description>
          {inUse ? t('tagsAlocacao:excluir.emUso') : t('tagsAlocacao:excluir.descricao')}
        </AlertDialog.Description>
        <AlertDialog.Footer>
          <AlertDialog.Cancel type="button" disabled={deleteMutation.isPending}>
            {inUse ? t('common:close') : t('common:cancel')}
          </AlertDialog.Cancel>
          {!inUse && (
            <AlertDialog.Action
              type="button"
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={confirm}
            >
              {t('tagsAlocacao:excluir.confirmar')}
            </AlertDialog.Action>
          )}
        </AlertDialog.Footer>
      </AlertDialog.Content>
    </AlertDialog>
  );
}
